import { z } from 'zod';
import { parseWebApiResponse } from './common';
const stringOrDateSchema = z.union([z.string(), z.date()]);
const nullableStringSchema = z.string().nullable().optional();
const nullableNumberSchema = z.coerce.number().nullable().optional();
const entityWithToJson = z.custom((value) => Boolean(value &&
    typeof value === 'object' &&
    'toJSON' in value &&
    typeof value.toJSON === 'function'));
export function toPlainWebApiData(data) {
    if (Array.isArray(data)) {
        return data.map((item) => toPlainWebApiData(item));
    }
    const entity = entityWithToJson.safeParse(data);
    if (entity.success) {
        return entity.data.toJSON();
    }
    return data;
}
const portfolioSummarySchema = z
    .object({
    id: z.coerce.number(),
    name: nullableStringSchema,
})
    .passthrough();
const buildingSummarySchema = z
    .object({
    id: z.coerce.number(),
    streetNumber: nullableStringSchema,
    streetName: nullableStringSchema,
})
    .passthrough();
const personSchema = z
    .object({
    id: z.coerce.number(),
    firstName: nullableStringSchema,
    lastName: nullableStringSchema,
    email: nullableStringSchema,
    phoneNumberCell: nullableStringSchema,
    peopleType: nullableStringSchema,
})
    .passthrough();
const tenantStatusSchema = z
    .object({
    status: z.string().nullable().optional(),
    person: personSchema.nullable().optional(),
    people: personSchema.nullable().optional(),
})
    .passthrough();
const leaseSchema = z
    .object({
    id: z.coerce.number(),
    apartmentId: nullableNumberSchema,
    rent: nullableNumberSchema,
    leaseStartDate: stringOrDateSchema.nullable().optional(),
    leaseEndDate: stringOrDateSchema.nullable().optional(),
    createdAt: stringOrDateSchema.nullable().optional(),
    tenants: z.array(personSchema).optional().default([]),
})
    .passthrough();
const unitSummarySchema = z
    .object({
    id: z.coerce.number(),
    unitNumber: nullableStringSchema,
})
    .passthrough();
export const buildingsListResponseSchema = z.array(buildingSummarySchema
    .extend({
    legalStructure: nullableStringSchema,
    portfolio: portfolioSummarySchema,
    apartments: z.array(unitSummarySchema),
})
    .passthrough());
export const buildingProfileResponseSchema = buildingSummarySchema
    .extend({
    portfolio: portfolioSummarySchema.optional(),
    apartments: z.array(unitSummarySchema
        .extend({
        currentResidents: z.unknown().optional(),
        tenantStatuses: z.array(tenantStatusSchema).optional().default([]),
        leases: z.array(leaseSchema).optional().default([]),
        apartmentOwners: z.array(personSchema).optional().default([]),
    })
        .passthrough()),
})
    .passthrough();
export const unitsListResponseSchema = z.array(unitSummarySchema
    .extend({
    building: buildingSummarySchema
        .extend({
        portfolio: portfolioSummarySchema.pick({ id: true }).passthrough().optional(),
    })
        .passthrough(),
})
    .passthrough());
export const unitProfileResponseSchema = unitSummarySchema
    .extend({
    createdAt: stringOrDateSchema.nullable().optional(),
    building: buildingSummarySchema
        .extend({
        portfolio: portfolioSummarySchema.pick({ id: true }).passthrough(),
    })
        .passthrough(),
    tenantStatuses: z.array(tenantStatusSchema).optional().default([]),
    leases: z.array(leaseSchema).optional().default([]),
    apartmentOwners: z.array(personSchema).optional().default([]),
})
    .passthrough();
export const unitFormattedAddressResponseSchema = z.string();
export const unitLastLeaseResponseSchema = leaseSchema
    .pick({
    id: true,
    leaseStartDate: true,
    leaseEndDate: true,
})
    .passthrough()
    .nullable();
export const portfolioDetailsResponseSchema = portfolioSummarySchema.nullable();
export function parseBuildingsListResponse(data) {
    return parseWebApiResponse(buildingsListResponseSchema, data, 'GET /api/buildings');
}
export function parseBuildingProfileResponse(data) {
    return parseWebApiResponse(buildingProfileResponseSchema, data, 'GET /api/buildings/:id/profile');
}
export function parseUnitsListResponse(data) {
    return parseWebApiResponse(unitsListResponseSchema, data, 'GET /api/units');
}
export function parseUnitProfileResponse(data) {
    return parseWebApiResponse(unitProfileResponseSchema, data, 'GET /api/units/:id/profile');
}
export function parseUnitFormattedAddressResponse(data) {
    return parseWebApiResponse(unitFormattedAddressResponseSchema, data, 'GET /api/units/:id/formatted-address');
}
export function parseUnitLastLeaseResponse(data) {
    return parseWebApiResponse(unitLastLeaseResponseSchema, data, 'GET /api/units/:id/last-lease');
}
export function parsePortfolioDetailsResponse(data) {
    return parseWebApiResponse(portfolioDetailsResponseSchema, data, 'GET /api/portfolios/:id');
}
